"use client";

import { useState } from 'react';
import Link from 'next/link';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useTheme } from 'next-themes';
import { 
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button
} from '../../components/ui';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import { Format, READABLE_FORMATS } from '../../lib/formats';
import { FileFormatIcon } from '../file-format';
import { AuthButtons } from '../auth/auth-buttons';

export interface NavItem {
  label: string;
  href: string;
}

const defaultNavItems: NavItem[] = [
  { label: 'Home', href: '/home' },
  { label: 'Agents', href: '/agents' },
  { label: 'Statistics', href: '/statistics' },
  { label: 'Graphs', href: '/graphs' },
];

interface HeaderProps {
  navItems?: NavItem[];
}

export function Header({ navItems = defaultNavItems }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme } = useTheme();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white/95 backdrop-blur dark:border-gray-800 dark:bg-gray-950/95">
      <div className="w-full flex h-16 items-center px-6 md:px-8 lg:px-12">
        {/* Logo/Brand and Navigation in one group */}
        <div className="flex items-center flex-1">
          <Link href="/" className="flex items-center space-x-2 mr-12">
            <span className="text-xl font-bold">TabLab</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              >
                {item.label}
              </Link>
            ))}
            
            {/* File Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white">
                File Tools <ChevronDown className="ml-1 h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="p-2">
                {READABLE_FORMATS.map((format) => (
                  <DropdownMenuItem key={format} asChild>
                    <Link 
                      href={`/${format.toLowerCase()}`} 
                      className="w-full cursor-pointer px-3 py-2 rounded-md text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 flex items-center"
                    >
                      <FileFormatIcon format={format} size={16} className="mr-2" />
                      {format.toUpperCase()}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
        
        {/* Auth Buttons and Theme Toggle - Far Right */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Submit Feedback Button */}
          <Button variant="ghost" size="sm" asChild>
            <Link href="https://tablab.featurebase.app" target="_blank" rel="noopener noreferrer">
              Submit Feedback
            </Link>
          </Button>
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Auth Buttons */}
          <AuthButtons 
            signInVariant="ghost" 
            signUpVariant="outline" 
            showGoogleSignIn={true}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center md:hidden">
          {/* Mobile Theme Toggle */}
          <ThemeToggle className="mr-2" />
          
          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="rounded-full p-2 text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden">
          <div className="container mx-auto px-4 py-4 space-y-4">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="block py-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* File Tools */}
            <div className="py-2">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                File Tools
              </div>
              <div className="grid grid-cols-2 gap-2">
                {READABLE_FORMATS.map((format) => (
                  <Link
                    key={format}
                    href={`/${format.toLowerCase()}`}
                    className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <FileFormatIcon format={format} size={16} className="mr-2" />
                    {format.toUpperCase()}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Submit Feedback Button for Mobile */}
            <Link
              href="https://tablab.featurebase.app"
              className="block py-2 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
            >
              Submit Feedback
            </Link>
            
            {/* Mobile Auth Buttons */}
            <div className="py-2">
              <AuthButtons 
                signInVariant="ghost" 
                signUpVariant="outline" 
                showGoogleSignIn={true}
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
} 